import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import fr from 'facet-rest';
import useComponentDidMount from 'src/Hooks/useComponentDidMount';
import { dispatchModalCollectionCleared } from 'Hooks/useRefreshEvent';

export const MODAL_SEARCH_PARAM_KEY = 'modalSelfLink';
const ROUTE_STATE_KEY = 'wasPushedByModalContext';

const ModalContext = React.createContext(undefined);

// Actions
const CLEAR_MODAL_COLLECTION = 'clear-modal-collection';
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';
const MODAL_MOUNTED = 'modal-mounted';
const MODAL_REFRESH_STARTED = 'modal-refresh-started';
const MODAL_REFRESH_SUCCEEDED = 'modal-refresh-succeeded';
const MODAL_REFRESH_FAILED = 'modal-refresh-failed';
const SET_MODAL_COLLECTION = 'set-modal-collection';

const init = () => {
  return {
    isMounted: false,
    isInitializing: false,
    initializingError: null,
    modalCollection: null,
    modalSelfLink: null,
    isModalRefreshing: false,
    modalRefreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case CLEAR_MODAL_COLLECTION: {
      return {
        ...state,
        modalCollection: null,
        modalSelfLink: null,
      };
    }
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
        modalSelfLink: action.modalSelfLink,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        modalCollection: action.modalCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case MODAL_MOUNTED: {
      return { ...state, isMounted: true };
    }
    case MODAL_REFRESH_STARTED: {
      return {
        ...state,
        isModalRefreshing: true,
        modalRefreshError: null,
        modalSelfLink: action.modalSelfLink,
      };
    }
    case MODAL_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isModalRefreshing: false,
        modalRefreshError: null,
        modalCollection: action.modalCollection,
      };
    }
    case MODAL_REFRESH_FAILED: {
      return {
        ...state,
        isModalRefreshing: false,
        modalRefreshError: action.modalRefreshError,
      };
    }
    case SET_MODAL_COLLECTION: {
      // React's useState has a pattern where you can either give it a value or you can give it a function that takes in the current value and transforms it.
      // We're replicating that here.
      const modalCollection = typeof action.modalCollection === 'function'
        ? action.modalCollection(state.modalCollection)
        : action.modalCollection;
      return {
        ...state,
        modalCollection,
        modalSelfLink: modalCollection.link('self').url(),
      };
    }
    default: {
      throw new Error(`Unhandled ModalContext action type: ${action.type}`);
    }
  }
};

const ModalProvider = ({ children }) => {
  const history = useHistory();
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const actionsRef = React.useRef(null);
  const titleRef = React.useRef(null);

  const isRouteChangeModalSelfLinkUpdate = history.location.state?.[ROUTE_STATE_KEY] && history.action !== 'POP';
  const searchParams = new URLSearchParams(history.location.search);
  const modalSelfLinkSearchParam = searchParams.get(MODAL_SEARCH_PARAM_KEY);
  const { isMounted, modalCollection, modalSelfLink } = state;

  const clearModalCollection = React.useCallback(() => {
    if (modalSelfLink === null) {
      return;
    }

    dispatchModalCollectionCleared(modalCollection);

    dispatch({
      type: CLEAR_MODAL_COLLECTION,
    });
  }, [modalSelfLink, modalCollection]);

  const loadModalCollection = React.useCallback(
    async link => {
      if (modalSelfLink === link) {
        return;
      }

      try {
        dispatch({
          type: INITIALIZE_STARTED,
          modalSelfLink: link,
        });

        const resp = await fr.http.get(link);

        if (resp.resources().length() === 0) {
          resp.create({
            addToCollection: true,
          });
        }

        dispatch({
          type: INITIALIZE_SUCCEEDED,
          modalCollection: resp,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error loading modal context: ', err);
        dispatch({
          type: INITIALIZE_FAILED,
          initializationError: err,
        });
      }
    },
    [modalSelfLink],
  );

  const refreshModalCollection = React.useCallback(async () => {
    if (modalCollection === null) {
      return;
    }

    if (modalCollection.resources().length() === 0) {
      return;
    }

    try {
      dispatch({ type: MODAL_REFRESH_STARTED, modalSelfLink: modalCollection.link('self').url() });
      const coll = await modalCollection.link('self').get();
      dispatch({ type: MODAL_REFRESH_SUCCEEDED, modalCollection: coll });
    } catch (err) {
      dispatch({ type: MODAL_REFRESH_FAILED, modalRefreshError: err });
    }
  }, [modalCollection]);

  const setModalCollection = React.useCallback(nextModalCollection => {
    if (typeof nextModalCollection === 'object' && nextModalCollection.link('self').url() === modalSelfLink) {
      return;
    }

    dispatch({
      type: SET_MODAL_COLLECTION,
      modalCollection: nextModalCollection,
    });
  }, []);

  const memoizedValue = React.useMemo(() => {
    const modalResource = state.modalCollection
      ? state.modalCollection.resources().head()
      : null;

    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      modalCollection: state.modalCollection,
      modalResource,
      isModalRefreshing: state.isModalRefreshing,
      modalRefreshError: state.modalRefreshError,
      clearModalCollection,
      loadModalCollectionByLink: loadModalCollection,
      refreshModalCollection,
      setModalCollection,
      actionsRef,
      titleRef,
    };
  }, [
    actionsRef,
    clearModalCollection,
    loadModalCollection,
    refreshModalCollection,
    setModalCollection,
    state,
    titleRef,
  ]);

  const updateModalState = nextLink => {
    if (nextLink === null) {
      clearModalCollection();
    } else {
      loadModalCollection(nextLink);
    }
  };

  const updateModalSearchParam = newLink => {
    if (newLink === null) {
      if (modalSelfLinkSearchParam === null) {
        return;
      }

      searchParams.delete(MODAL_SEARCH_PARAM_KEY);
    } else {
      if (newLink === modalSelfLinkSearchParam) {
        return;
      }

      searchParams.set(MODAL_SEARCH_PARAM_KEY, newLink);
    }

    history.push(
      {
        search: searchParams.toString(),
      },
      { [ROUTE_STATE_KEY]: true },
    );
  };

  React.useEffect(() => {
    if (!isMounted) {
      return;
    }

    updateModalSearchParam(modalSelfLink);
  }, [modalSelfLink]);

  React.useEffect(() => {
    if (isRouteChangeModalSelfLinkUpdate) {
      return;
    }

    updateModalState(modalSelfLinkSearchParam);
  }, [modalSelfLinkSearchParam]);

  useComponentDidMount(() => {
    dispatch({ type: MODAL_MOUNTED });
  });

  return (
    <ModalContext.Provider value={memoizedValue}>
      {children}
    </ModalContext.Provider>
  );
};

// Use Modal State Context
const useModalContext = () => {
  const context = React.useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
};

ModalProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  ModalProvider,
  useModalContext,
};
